/**
 * Orbital Frontend - Main Entry Point
 *
 * This module serves as the entry point for Vite bundling.
 * It imports and exposes all modular functionality.
 */

// ============================================
// Core Modules
// ============================================
import * as events from "../../engine/core/events.js";
import * as dataModel from "../../engine/core/data-model.js";
import * as undoManager from "../../engine/core/undo-manager.js";

// ============================================
// Crypto Modules
// ============================================
import { E2EE, Vault, VaultStorage, initVaultStorage } from "../../engine/crypto/index.js";

// ============================================
// UI Modules
// ============================================
import * as theme from "./ui/theme.js";
import * as modals from "./ui/modals.js";
import * as search from "./ui/search.js";
import * as vaultUI from "./ui/vault/index.js";

// ============================================
// Integrations
// ============================================
import * as calendar from "./integrations/calendar.js";

// ============================================
// Views
// ============================================
import * as views from "./views/index.js";

// ============================================
// Auth
// ============================================
import * as auth from "./ui/auth/index.js";

// ============================================
// Store & Controller (New Architecture)
// ============================================
import * as store from "./store/index.js";
import * as controller from "./controller/index.js";

// ============================================
// New View Modules
// ============================================
import * as kanbanView from "./views/kanban/index.js";
import * as dashboardView from "./views/dashboard/index.js";
import * as bubbleView from "./views/bubble/index.js";
import * as mindmapView from "./views/mindmap/index.js";
import * as ganttView from "./views/gantt/index.js";
// NOTE: no notesView here — the notes view drags the TipTap/ProseMirror stack
// and is code-split out of the boot bundle (lazy-loaded via main.js's view
// registry). window.OrbitalNewViews has no consumers, so the entry was dropped
// rather than proxied.

// ============================================
// Application Bootstrap
// ============================================
import * as app from "../../engine/account.js";

// ============================================
// Sync Modules (CRDT)
// ============================================
import { ConnectionState, MessageType } from "../../engine/sync/websocket.js";
import * as crdtBridge from "../../engine/sync/crdt-bridge.js";


console.log("[Orbital] Module system initialized");

// ============================================
// Initialize Core Systems
// ============================================

// Undo/redo is now handled by engine/core/undo-manager.js, a thin wrapper around
// Y.UndoManager. Lifecycle (bind/setActive/unbind) is driven by crdt-bridge
// and share-lifecycle. The old snapshot-based core/history.js has been
// retired — it was not multi-device-safe.

// ============================================
// Expose modules globally for inline scripts
// ============================================

// Core modules - for gradual migration from inline code
window.OrbitalCore = {
  // Event system
  events: events.Events,
  on: events.on,
  once: events.once,
  emit: events.emit,

  // State management (delegating to Store)
  state: null, // Removed - use Store.getState() instead
  getState: store.get,
  getData: () => store.get('data'),

  // Data model operations (read-only pass-throughs)
  dataModel: {
    find: dataModel.find,
    findParent: dataModel.findParent,
    getPath: dataModel.getPath,
    getAll: dataModel.getAll,
    getLeaves: dataModel.getLeaves,
    cleanNode: dataModel.cleanNode,
    serialize: dataModel.serialize,
    deserialize: dataModel.deserialize,
  },

  // Undo / redo (Y.UndoManager-backed). Kept under `history` for backward
  // compatibility with any console scripts that referenced the old API.
  history: {
    undo: undoManager.undo,
    redo: undoManager.redo,
    canUndo: undoManager.canUndo,
    canRedo: undoManager.canRedo,
    clear: undoManager.clear,
    getActiveDocId: undoManager.getActiveDocId,
  },

  // Phase 3.3 (PLAN17): Expose debug utilities for performance monitoring
  debug: {
    getPerformanceMetrics: () => crdtBridge.getPerformanceMetrics?.() || null,
    getLogLevel: () => crdtBridge.getLogLevel?.() || 'info',
    setLogLevel: (level) => crdtBridge.setLogLevel?.(level)
  },

  // Recovery (docs/plans/2026-10-13-drop-schema-v1.md): the backups this
  // device took before dropping maps' old copies, and saving one's old copy
  // as a map export file (import it as a new map to compare).
  recovery: {
    listOldCopyBackups: () => import('../../engine/sync/migration-backups.js').then((m) => m.listOldCopyBackups()),
    saveOldCopy: async (docId) => {
      const [{ oldCopyFromBackup }, { saveFileFromBlob }] = await Promise.all([
        import('../../engine/sync/old-copy-drop.js'), import('./ui/save-file.js')]);
      const { blob, filename, nodes } = await oldCopyFromBackup(docId);
      await saveFileFromBlob(blob, filename, { throwOnError: true });
      return { saved: true, nodes };
    },
  },

};

// Sync modules (CRDT Bridge)
if (localStorage.getItem('orbital-debug') === 'true') {
  window.OrbitalSync = {
    // Constants
    ConnectionState,
    MessageType,

    // CRDT Bridge
    init: crdtBridge.init,
    startSync: crdtBridge.startSync,
    stopSync: crdtBridge.stopSync,
    getConnectionState: crdtBridge.getConnectionState,
    isSyncEnabled: crdtBridge.isSyncEnabled,
    getDocManager: crdtBridge.getDocManager,
    getSyncManager: crdtBridge.getSyncManager,
    destroy: crdtBridge.destroy,
  };
}

// Crypto modules omitted from window — sensitive key material should not be
// accessible from the console or third-party scripts.

// Vault UI - for multi-map file browser
window.OrbitalVaultUI = {
  init: vaultUI.init,
  open: vaultUI.open,
  close: vaultUI.close,
  toggle: vaultUI.toggle,
  render: vaultUI.render,
  updateCurrentDocName: vaultUI.updateCurrentDocName,
};

// Application Bootstrap - read-only accessors only (setters omitted for security)
window.OrbitalApp = {
  getCurrentUser: app.getCurrentUser,
};

// Theme module - for UI and color themes
window.OrbitalTheme = {
  // UI theme (light/dark/nord/sepia)
  getUITheme: theme.getUITheme,
  setUITheme: theme.setUITheme,
  loadUITheme: theme.loadUITheme,

  // Color themes (node palettes)
  COLOR_THEMES: theme.COLOR_THEMES,
  getColorTheme: theme.getColorTheme,
  setColorTheme: theme.setColorTheme,
  loadColorTheme: theme.loadColorTheme,
  getCurrentColors: theme.getCurrentColors,
  getColor: theme.getColor,
  ROOT_COLOR: theme.ROOT_COLOR,

  // Setup helpers
  initThemes: theme.initThemes,
  setupThemeDropdown: theme.setupThemeDropdown,
};

// NOTE: window.OrbitalKeyboard (ui/keyboard.js) was removed in the
// 2026-06-12 audit — it was a complete parallel shortcut system that was
// never initialized. The real shortcuts live in interfaces/classic/managers/keyboard-manager.js.

// Modals module
window.OrbitalModals = {
  // Creation
  createModal: modals.createModal,

  // Display
  showModal: modals.showModal,
  closeModal: modals.closeModal,
  closeAllModals: modals.closeAllModals,

  // State
  isModalOpen: modals.isModalOpen,
  getActiveModalId: modals.getActiveModalId,
  getModal: modals.getModal,

  // Lifecycle
  init: modals.initModals,
  destroy: modals.destroyModals,

  // Convenience
  confirm: modals.confirm,
  alert: modals.alert,
};

// Search module
window.OrbitalSearch = {
  // Initialization
  init: search.initSearch,
  setupUI: search.setupSearchUI,

  // Search
  search: search.search,
  highlightMatches: search.highlightMatches,

  // UI
  clear: search.clearSearch,
  focus: search.focusSearch,
  isActive: search.isSearchActive,
};

// Views module - view management infrastructure
window.OrbitalViews = {
  // View registry
  registerView: views.registerView,
  getView: views.getView,
  getCurrentView: views.getCurrentView,
  switchView: views.switchView,
  renderCurrentView: views.renderCurrentView,

  // Container management
  showViewContainer: views.showViewContainer,
  getViewContainer: views.getViewContainer,

  // Tab management
  updateTabs: views.updateTabs,
  setupTabs: views.setupTabs,

  // State sync
  syncViewState: views.syncViewState,
  getViewFromURL: views.getViewFromURL,

  // Utilities
  clearAllViews: views.clearAllViews,
};

// Auth module - read-only state queries only (setters omitted for security)
window.OrbitalAuth = {
  isLoggedIn: auth.isLoggedIn,
  hasEncryptionKey: auth.hasEncryptionKey,
  getCurrentScreen: auth.getCurrentScreen,
};

// Calendar integration module
window.OrbitalCalendar = {
  // Initialization
  init: calendar.initCalendar,
  checkLocalProxy: calendar.checkLocalProxy,
  isLocalProxyAvailable: calendar.isLocalProxyAvailable,

  // Configuration
  loadConfig: calendar.loadConfig,
  saveConfig: calendar.saveConfig,

  // Loading & display
  loadCalendar: calendar.loadCalendar,
  showLoading: calendar.showLoading,
  showSetup: calendar.showSetup,

  // Event fetching
  fetchCalendarEvents: calendar.fetchCalendarEvents,
  fetchICSEvents: calendar.fetchICSEvents,
  fetchCalDAVEvents: calendar.fetchCalDAVEvents,

  // Parsing & filtering
  parseICS: calendar.parseICS,
  filterEventsForDays: calendar.filterEventsForDays,

  // Modal
  openModal: calendar.openModal,
  closeModal: calendar.closeModal,
  addCalendar: calendar.addCalendar,
  saveSettings: calendar.saveSettings,
  setupEventListeners: calendar.setupEventListeners,

  // Constants
  CALENDAR_COLORS: calendar.CALENDAR_COLORS,
};

// The engine's API (engine/index.js), for checks and the console.
if (localStorage.getItem('orbital-debug') === 'true') {
  import("../../engine/index.js").then(({ engine }) => { window.OrbitalEngine = engine; });
}

// Store - Central state management (new architecture)
if (localStorage.getItem('orbital-debug') === 'true') {
  window.OrbitalStore = {
    // State access
    getState: store.getState,
    get: store.get,
    subscribe: store.subscribe,

    // Data actions
    initialize: store.initialize,
    addNode: crdtBridge.addNode,
    updateNode: crdtBridge.updateNode,
    removeNode: crdtBridge.removeNode,
    moveNode: crdtBridge.moveNode,

    // Navigation
    setFocus: store.setFocus,
    setSelected: store.setSelected,
    navigateUp: store.navigateUp,
    navigateInto: store.navigateInto,

    // View actions
    setView: store.setView,
    setHideCompleted: store.setHideCompleted,

    // Selectors
    getFocusedNode: store.getFocusedNode,
    getSelectedNode: store.getSelectedNode,
    getVisibleNodes: store.getVisibleNodes,
    getBreadcrumbs: store.getBreadcrumbs,

    // Data model passthrough
    find: store.find,
    findParent: store.findParent,
    getAll: store.getAll,

    // Reset
    reset: store.reset,
  };

  // Controller - App coordination (new architecture)
  window.OrbitalController = {
    init: controller.init,
    switchView: controller.switchView,
    renderView: controller.renderView,
    navigateTo: controller.navigateTo,
    navigateUp: controller.navigateUp,
    select: controller.select,
    addNode: controller.addNode,
    updateNode: controller.updateNode,
    deleteNode: controller.deleteNode,
    moveNode: controller.moveNode,
    undo: controller.undo,
    redo: controller.redo,
  };
}

// New view modules
window.OrbitalNewViews = {
  kanban: kanbanView,
  dashboard: dashboardView,
  bubble: bubbleView,
  mindmap: mindmapView,
  gantt: ganttView,
};

console.log("[Orbital] All modules loaded and ready");
